import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { matchPath, useNavigate, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { oidcUserManager } from '../../services/oidcService';
import { getSSOTenantsResult, loginWithSso, type Tenant } from '../../services/acceoConnectService';
import { useAuthStore } from '../../stores/authStore';
import { useConnectCredentialsStore } from '../../stores/session/connectCredentialsStore';
import { useTenantStore } from '../../stores/tenantStore';
import { useToastStore } from '../../stores/toastStore';
import { useTranslation } from 'react-i18next';
import { UserPopover } from './UserPopover';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { NoAiToggle } from './NoAiToggle';
import { TranslationKey } from '../../i18n/translationKeys';
import { IGNORED_RELANCES_ROUTE, UPCOMING_RELANCES_ROUTE } from '../../routes/paths';
import { useNavigationStackStore } from '../../stores/navigationStackStore';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { TenantPicker } from '../../components/TenantPicker/TenantPicker';
import { TenantPickerSkeleton } from '../../components/TenantPicker/TenantPickerSkeleton';
import { ConfirmModal } from '../../components/ConfirmModal/ConfirmModal';
import { useTasksStore } from '../../stores/tasksStore';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useCustomEmailTemplateStore } from '../../stores/customEmailTemplateStore';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { AcombaBridgeIcon } from './AcombaBridge/AcombaBridgeIcon';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { useAcombaBridgeAccessReady } from '../../hooks/useAcombaBridgeAccessReady';
import { useSessionStore } from '../../stores/session/sessionStore';
import { syncService } from '../../services/syncService';
import { establishBrowserAppSession, revokeAppSession } from '../../services/appSession';
import { useSyncStore } from '../../stores/syncStore';

// Shared chrome for the header's text-only actions (history, profile) — a plain-labelled control
// that reads as secondary next to the icon buttons beside it.
const HEADER_ACTION_BTN_CLASS =
    'h-9 px-1.5 flex items-center justify-center rounded-md border border-transparent text-content-subtle text-s font-medium ' +
    'hover:underline hover:decoration-1 hover:underline-offset-2 disabled:text-content-tertiary disabled:no-underline disabled:cursor-not-allowed ' +
    'hover:cursor-pointer transition-colors focus-visible:outline-none focus-visible:border-focus focus-visible:ring-4 focus-visible:ring-focus-ring';

// Static fallback parents used when the navigation stack is empty.
const STATIC_PARENTS: Array<{
    pattern: string;
    parentPath: string;
    parentLabelKey: string;
}> = [
        { pattern: '/customer-follow-up/:id', parentPath: '/', parentLabelKey: TranslationKey.Header.HomeName },
        { pattern: '/history', parentPath: '/', parentLabelKey: TranslationKey.Header.HomeName },
        { pattern: '/settings', parentPath: '/', parentLabelKey: TranslationKey.Header.HomeName },
        { pattern: '/acomba', parentPath: '/', parentLabelKey: TranslationKey.Header.HomeName },
        { pattern: IGNORED_RELANCES_ROUTE, parentPath: '/', parentLabelKey: TranslationKey.Header.HomeName },
        { pattern: UPCOMING_RELANCES_ROUTE, parentPath: '/', parentLabelKey: TranslationKey.Header.HomeName },
    ];

export const AppHeader: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const appNavigate = useAppNavigate();
    const location = useLocation();
    const isHome = location.pathname === '/';
    const isHistory = !!matchPath('/history', location.pathname);

    const { stack, pop } = useNavigationStackStore(
        useShallow((state) => ({ stack: state.stack, pop: state.pop }))
    );
    const stackTop = stack[stack.length - 1];

    const staticParent = useMemo(() => {
        for (const route of STATIC_PARENTS) {
            if (matchPath(route.pattern, location.pathname)) {
                return { path: route.parentPath, label: t(route.parentLabelKey) };
            }
        }
        return null;
    }, [location.pathname, t]);

    const backDest = stackTop
        ? { path: stackTop.path, label: t(stackTop.labelKey) }
        : staticParent ?? { path: '/', label: t(TranslationKey.Header.HomeName) };

    const handleBack = () => {
        if (stackTop) {
            const entry = pop();
            if (entry) void navigate(entry.path, { state: entry.state });
        } else {
            void navigate(backDest.path);
        }
    };

    const [popoverOpen, setPopoverOpen] = useState(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [changingCompany, setChangingCompany] = useState(false);
    const [ccTenants, setCcTenants] = useState<Tenant[] | null>(null);
    const [ccConnectUnavailable, setCcConnectUnavailable] = useState(false);
    const [ccToken, setCcToken] = useState<string | null>(null);
    const profileBtnRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const { user } = useAuthStore();
    const { setAcceoConnectToken, setSaasSessionToken, portalIsToken } = useConnectCredentialsStore();
    const { companyName: storedCompanyName, clearTenant, setTenant } = useTenantStore();
    const { clearTasks, dailyTasks, fetchDailyTasks } = useTasksStore();
    const { reset: resetRelanceDeactivation } = useRelanceDeactivationStore();
    const resetCustomEmailTemplate = useCustomEmailTemplateStore(s => s.reset);
    const { showToast } = useToastStore();
    const logoutInProgress = useRef(false);

    // Persisted backend decision (see TenantSectionAccessEntity) — never a live bridge read. Fetched
    // here (not only from Home) so the signal is available on any route, including a direct /history
    // visit. Fails open (true) until the first fetch resolves, consistent with "unknown ≠ denied".
    const relanceAccessible = dailyTasks?.relanceAccessible ?? true;

    const isAcomba = useIsAcomba();
    // See useAcombaBridgeAccessReady — /tasks/daily must not be read before the bridge's first health
    // check has had a chance to persist the current Usager's access decision.
    const bridgeAccessReady = useAcombaBridgeAccessReady();
    useEffect(() => {
        if (dailyTasks === null && bridgeAccessReady) void fetchDailyTasks();
    }, [dailyTasks, fetchDailyTasks, bridgeAccessReady]);

    const email = user?.email ?? '';

    // In Acomba mode the bridge is the live source of truth for the open société — its name comes
    // straight from the Acomba SDK. Prefer it over the tenant store, whose companyName is resolved
    // from Catalog and can be empty for a desktop-launched session. Fall back to the stored name
    // (and finally '') when the bridge is not connected or hasn't reported a company yet.
    const bridgeCompanyName = useAcombaBridgeStore((s) => s.companyName);
    const companyName = (isAcomba ? bridgeCompanyName : null) ?? storedCompanyName ?? '';
    // The Acomba desktop launch fixes the société to whatever is open in Acomba, so — like the
    // AcombaGo portal — there is no "change company" action in that session.
    const isDesktopSession = useSessionStore((s) => s.sessionKind === 'desktop');
    const setInitialSyncRequired = useSyncStore(s => s.setInitialSyncRequired);
    const resetSync = useSyncStore(s => s.resetSync);

    const handleLogout = () => {
        setPopoverOpen(false);
        setLogoutConfirmOpen(true);
    };

    const handleLogoutCancel = () => setLogoutConfirmOpen(false);

    const handleLogoutConfirm = () => {
        if (logoutInProgress.current) return;
        logoutInProgress.current = true;
        setLogoutConfirmOpen(false);
        // Best-effort server-side revocation first, regardless of outcome — see revokeAppSession's doc.
        // Stores are deliberately left untouched here: clearing them triggers the auth guard, which
        // races signoutRedirect and lands the user on the company selector. SignoutCallback owns cleanup.
        void revokeAppSession().finally(() => {
            void oidcUserManager.signoutRedirect().catch(() => {
                logoutInProgress.current = false;
            });
        });
    };

    const handleChangeCompany = async () => {
        const oidcUser = await oidcUserManager.getUser();
        if (!oidcUser || oidcUser.expired) {
            void oidcUserManager.signinRedirect({ prompt: 'login', max_age: 0 });
            return;
        }
        const token = oidcUser.access_token;
        setChangingCompany(true);
        setCcToken(token);
        try {
            const { tenants: tenantList, connectUnavailable } = await getSSOTenantsResult(token);
            setCcConnectUnavailable(connectUnavailable);
            setCcTenants(tenantList);
        } catch {
            setChangingCompany(false);
            setCcToken(null);
            showToast('error', t(TranslationKey.Header.ChangeCompanyError));
        }
    };

    const handleCcTenantSelect = async (tenant: Tenant) => {
        if (!ccToken) return;
        try {
            const result = await loginWithSso(ccToken, tenant.tenantCode, tenant.username, tenant.ciu);
            if (!(await establishBrowserAppSession(ccToken, tenant.ciu, tenant.tenantCode))) {
                setChangingCompany(false);
                setCcTenants(null);
                setCcToken(null);
                showToast('error', t(TranslationKey.Header.ChangeCompanyError));
                return;
            }
            clearTenant();
            clearTasks();
            resetRelanceDeactivation();
            resetCustomEmailTemplate();
            // Company-scoped like the tasks above: without this, a sync still running for the company
            // being left keeps its progress on screen under the new company's name.
            resetSync();
            setAcceoConnectToken(result.access_token, result.expires_in);
            setSaasSessionToken(result.saasSessionToken ?? null);
            setTenant(tenant.tenantCode, tenant.companyName, tenant.ciu);
            setChangingCompany(false);
            setCcTenants(null);
            setCcToken(null);
            try {
                const status = await syncService.getStatus();
                if (!status.hasEverSynced) {
                    setInitialSyncRequired(true);
                }
            } catch {
                // Fail-open: hasEverSynced treated as true
            }
            void navigate('/');
        } catch {
            setChangingCompany(false);
            setCcTenants(null);
            setCcToken(null);
            showToast('error', t(TranslationKey.Header.ChangeCompanyError));
        }
    };

    const handleProfileBtnKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setPopoverOpen((v) => !v);
        } else if (e.key === 'Escape') {
            setPopoverOpen(false);
        }
    };

    React.useEffect(() => {
        if (!popoverOpen) return;
        function handleClick(e: MouseEvent) {
            if (
                profileBtnRef.current &&
                !profileBtnRef.current.contains(e.target as Node) &&
                popoverRef.current &&
                !popoverRef.current.contains(e.target as Node)
            ) {
                setPopoverOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [popoverOpen]);

    return (
        <>
            <header className="flex items-center justify-between gap-s text-primary-1100 pb-s">
                {/* Back button — shown on non-root pages */}
                <div className="flex-1 flex items-center">
                    {!isHome && (
                        <LinkButton
                            variant="navigation"
                            chevron="start"
                            size="s"
                            weight="medium"
                            data-testid="header-back-btn"
                            onClick={handleBack}
                            className="-ml-xs whitespace-nowrap"
                        >
                            {backDest.label}
                        </LinkButton>
                    )}
                </div>

                {/* Right actions */}
                <div className="flex items-center gap-xs relative -mr-xs">
                    <NoAiToggle />
                    {relanceAccessible && (
                        <button
                            type="button"
                            data-testid="header-history-btn"
                            disabled={isHistory}
                            onClick={() => appNavigate('/history')}
                            className={HEADER_ACTION_BTN_CLASS}
                        >
                            {t(TranslationKey.Header.History)}
                        </button>
                    )}
                    <div className="relative">
                        <button
                            ref={profileBtnRef}
                            data-testid="header-user-menu-btn"
                            className={HEADER_ACTION_BTN_CLASS}
                            aria-label={t(TranslationKey.Common.UserMenuAriaLabel)}
                            aria-haspopup="menu"
                            aria-expanded={popoverOpen}
                            tabIndex={0}
                            onClick={() => setPopoverOpen((v) => !v)}
                            onKeyDown={handleProfileBtnKeyDown}
                        >
                            {t(TranslationKey.Header.Profile)}
                        </button>
                        {popoverOpen && (
                            <div
                                ref={popoverRef}
                                style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, minWidth: 240, maxWidth: 240 }}
                                className="z-50"
                            >
                                <UserPopover
                                    companyName={companyName}
                                    email={email}
                                    canChangeCompany={!portalIsToken && !isDesktopSession}
                                    canLogout={!portalIsToken}
                                    onSettings={() => { setPopoverOpen(false); appNavigate('/settings'); }}
                                    onChangeCompany={() => { setPopoverOpen(false); void handleChangeCompany(); }}
                                    onLogout={handleLogout}
                                />
                            </div>
                        )}
                    </div>
                    {isAcomba && <AcombaBridgeIcon />}
                </div>
            </header>
            {logoutConfirmOpen && createPortal(
                <ConfirmModal
                    heading={t(TranslationKey.Header.LogoutConfirmDialog.Heading)}
                    body={t(TranslationKey.Header.LogoutConfirmDialog.Body)}
                    confirmLabel={t(TranslationKey.Header.LogoutConfirmDialog.Confirm)}
                    cancelLabel={t(TranslationKey.Header.LogoutConfirmDialog.Cancel)}
                    onConfirm={handleLogoutConfirm}
                    onCancel={handleLogoutCancel}
                    modalTestId="logout-confirm-modal"
                    confirmBtnTestId="logout-confirm-btn"
                />,
                document.body,
            )}
            {changingCompany && createPortal(
                <div className="fixed inset-0 z-[9999]">
                    {ccTenants === null ? (
                        <TenantPickerSkeleton label={t(TranslationKey.Header.ChangeCompanyLoading)} />
                    ) : (
                        <TenantPicker
                            tenants={ccTenants}
                            onSelect={(t) => { void handleCcTenantSelect(t); }}
                            connectUnavailable={ccConnectUnavailable}
                        />
                    )}
                </div>,
                document.body,
            )}
        </>
    );
};
